import {
  RUNS_PATH,
  RUN_REFUSALS,
  RUN_REFUSAL_STATUS,
  runPath,
  type PartyMember,
  type RunListing,
  type RunRefusal,
} from '@d2/schema';

/**
 * Everything this client asks the **game** server over HTTP, and nothing else.
 *
 * ## Why there is a second network client in this package at all
 *
 * `account/api.ts` talks to `apps/api`, which owns the database. This talks to
 * `apps/game`, which owns the live rooms — and only it can answer *"which runs
 * are open right now"*, because a run exists in a process and not in a table.
 * They are two services on two origins (`d2.ronaldswets.nl` and
 * `game.d2.ronaldswets.nl`), so one client with two base URLs would be a client
 * whose refusals mean different things depending on which half of it was
 * called.
 *
 * The two routes are ordinary `GET`s rather than matchmaking calls, and that is
 * the framework's doing rather than a preference: Colyseus 0.17 exposes
 * `POST /matchmake/:method/:roomName` and nothing else, and its SDK dropped
 * `getAvailableRooms` (`@d2/schema`'s `RUNS_PATH`). Both want the player's
 * access token, because the listing publishes the character names of everybody
 * in a party.
 *
 * ## There is no push channel, and this does not pretend otherwise
 *
 * A directory is read when somebody asks for it. No socket, no polling loop, no
 * subscription: a run that filled a second ago is discovered at the moment of
 * the click, by the *other* route, which is why the list deliberately carries no
 * room id (see `addressOf`).
 *
 * ## Every failure is a returned result, and four of them are not failures
 *
 * Nothing here throws — a caller that had to wrap this in a `try` is a caller
 * that will forget once and take the page down with an unhandled rejection. And
 * of the five things that can come back, only one is *"the game is not
 * answering"*: a full party, a run that has ended, a typo and a session that
 * expired are all a healthy server answering precisely, and this project has
 * now reported a healthy server as down three separate times
 * (`join-refusal.ts`). Collapsing them here would be the fourth.
 *
 * ## Nothing here logs
 *
 * The same rule `account/api.ts` keeps, for the same reason: the value passing
 * through is a bearer token, and a log line is the one place it would outlive
 * the request.
 */

/**
 * Why the directory would not answer, as far as this client is concerned.
 *
 * `RunRefusal` is `@d2/schema`'s — the four the server states — plus the one it
 * cannot state, because a server that is not answering says nothing at all.
 */
export type RunRefusalReason = RunRefusal | 'unreachable';

export type RunResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly refusal: RunRefusalReason };

export interface RunDirectory {
  /** Every public run with a seat left, as it stands at the moment of asking. */
  readonly listRuns: (token: string) => Promise<RunResult<readonly RunListing[]>>;
  /**
   * The **room id** an invite code names — the one thing a client feeds to
   * `joinById`, and the only place one crosses to a client that did not create
   * the room.
   *
   * Asked afresh per join on purpose. The listing carries no room id
   * (`RunListing`), so there is nothing cached to reach for, and the fullness
   * check the server runs while answering this *is* the check that matters:
   * anything read earlier describes a party as it was when the list was drawn.
   */
  readonly addressOf: (token: string, code: string) => Promise<RunResult<string>>;
}

export interface RunDirectoryOptions {
  /** Injected so the unit suite can drive one without a socket; a browser passes nothing. */
  readonly fetch?: typeof globalThis.fetch;
  /** Where the game server is, as an origin — `game/launch.ts`'s `gameOrigin`. */
  readonly baseUrl: string;
}

/**
 * The refusal each status carries, derived from the table both sides read
 * rather than written out again.
 *
 * It is the fallback and not the answer: the body states the refusal, and this
 * is what a proxy that swallowed the body leaves behind. `@d2/schema` declares
 * the mapping once precisely so a client that branched on the status cannot
 * reach a different conclusion from one that read the body — `run-full` is a
 * 409 and `no-such-run` a 404, so the two cannot be collapsed by accident.
 */
const REFUSAL_BY_STATUS: ReadonlyMap<number, RunRefusal> = new Map(
  RUN_REFUSALS.map((refusal) => [RUN_REFUSAL_STATUS[refusal], refusal]),
);

const isRunRefusal = (value: unknown): value is RunRefusal =>
  typeof value === 'string' && RUN_REFUSALS.some((refusal) => refusal === value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A number a screen can put in front of a player.
 *
 * `Number.isFinite` rather than a bare `typeof`, because `JSON.parse` answers
 * `1e999` with `Infinity` — which would draw a party of ∞ and a floor number
 * nobody could read.
 */
const isFigure = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const memberOf = (value: unknown): PartyMember | null => {
  if (!isRecord(value)) return null;

  const { name, level } = value;

  return typeof name === 'string' && isFigure(level) ? { name, level } : null;
};

/**
 * One run, off the wire — **built field by field**, never spread.
 *
 * The same discipline `describeRun` keeps on the server side and for the same
 * reason pointed the other way: what a listing is allowed to carry is the whole
 * security decision behind this feature, and a client that carried through
 * whatever arrived would hold a room id the moment one ever appeared on that
 * route. Something downstream would then use it, and the fresh fullness check
 * would be gone (`addressOf`).
 */
const listingOf = (value: unknown): RunListing | null => {
  if (!isRecord(value)) return null;

  const { code, depth, players, maxPlayers, party } = value;
  if (typeof code !== 'string' || code.length === 0) return null;
  if (!isFigure(depth) || !isFigure(players) || !isFigure(maxPlayers)) return null;
  if (!Array.isArray(party)) return null;

  const members = party.map(memberOf);
  if (members.includes(null)) return null;

  return { code, depth, players, maxPlayers, party: members as readonly PartyMember[] };
};

/**
 * Every run in the body, or `null` when one of them could not be read.
 *
 * One unreadable row fails the whole directory, exactly as one unreadable
 * character fails the whole roster: a list silently short by one is a player
 * who thinks their friend's run has ended.
 */
const listingsOf = (body: unknown): readonly RunListing[] | null => {
  if (!isRecord(body) || !Array.isArray(body['runs'])) return null;

  const rows = body['runs'].map(listingOf);

  return rows.includes(null) ? null : (rows as readonly RunListing[]);
};

const roomIdOf = (body: unknown): string | null => {
  if (!isRecord(body)) return null;

  const roomId = body['roomId'];

  return typeof roomId === 'string' && roomId.length > 0 ? roomId : null;
};

/** What came back, once. `null` is "nothing came back", which is a refusal of its own. */
interface Answer {
  readonly status: number;
  readonly body: unknown;
}

const unreachable = <T>(): RunResult<T> => ({ ok: false, refusal: 'unreachable' });

export const createRunDirectory = ({ fetch = globalThis.fetch, baseUrl }: RunDirectoryOptions): RunDirectory => {
  const root = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  const read = async (path: string, token: string): Promise<Answer | null> => {
    let response: Response;

    try {
      response = await fetch(`${root}${path}`, {
        method: 'GET',
        // Built here rather than by a caller, so there is exactly one place a
        // token is ever put on a request to the game server.
        headers: new Headers({ accept: 'application/json', authorization: `Bearer ${token}` }),
      });
    } catch {
      // A DNS failure, a refused connection, a mixed-content block. All the same
      // thing to a player: the game is not answering.
      return null;
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      // A proxy's HTML error page wearing a status, or an empty body.
      body = undefined;
    }

    return { status: response.status, body };
  };

  /** Why a non-200 was a non-200: what the server said, then what the status says. */
  const refusalOf = (answer: Answer): RunRefusalReason => {
    const stated = isRecord(answer.body) ? answer.body['refusal'] : undefined;
    if (isRunRefusal(stated)) return stated;

    return REFUSAL_BY_STATUS.get(answer.status) ?? 'unreachable';
  };

  return {
    listRuns: async (token: string): Promise<RunResult<readonly RunListing[]>> => {
      const answer = await read(RUNS_PATH, token);
      if (answer === null) return unreachable();

      if (answer.status === 200) {
        const runs = listingsOf(answer.body);

        return runs === null ? unreachable() : { ok: true, value: runs };
      }

      return { ok: false, refusal: refusalOf(answer) };
    },

    addressOf: async (token: string, code: string): Promise<RunResult<string>> => {
      // `runPath` percent-encodes, for the reason `internalSnapshotPath` does:
      // the caller supplies an identifier, never a route.
      const answer = await read(runPath(code), token);
      if (answer === null) return unreachable();

      if (answer.status === 200) {
        const roomId = roomIdOf(answer.body);

        return roomId === null ? unreachable() : { ok: true, value: roomId };
      }

      return { ok: false, refusal: refusalOf(answer) };
    },
  };
};
